/**
 * Píxel de Meta (dataset de Events Manager) — mide qué campañas de
 * Instagram/Facebook traen registros y partidas, no solo clics.
 *
 * <p>Es una cookie de **publicidad** de terceros: gated por el consentimiento
 * `marketing` (categoría propia del banner desde la v3, separada de
 * analíticas). Solo arranca en el dominio de producción real y con
 * `VITE_META_PIXEL_ID` definido; fuera de eso es no-op. Nunca corre en la
 * app nativa (el hostname es `localhost` bajo Capacitor) — allí exigiría
 * ATT de Apple.</p>
 *
 * <p>Las navegaciones SPA las cuenta el propio `fbevents.js` (escucha
 * `history.pushState` y emite `PageView`), así que aquí solo va el
 * `PageView` inicial. La captura automática de clics (`autoConfig`) va
 * desactivada. Spec: `docs/specs/2026-09-25-pixel-meta.md`.</p>
 */

const PROD_HOSTS = new Set(['matchplay.es', 'www.matchplay.es'])
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: FbqFn
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

let initialized = false

function pixelId(): string | undefined {
  const id = import.meta.env.VITE_META_PIXEL_ID as string | undefined
  return id?.trim() || undefined
}

/**
 * Carga el píxel una vez por página, o re-concede el consentimiento si ya
 * estaba cargado y se había revocado. No-op fuera de prod o sin ID.
 *
 * @returns true si el píxel está activo tras la llamada.
 */
export function initMetaPixel(): boolean {
  if (initialized) {
    window.fbq?.('consent', 'grant')
    return true
  }
  if (typeof window === 'undefined') return false
  if (!PROD_HOSTS.has(window.location.hostname)) return false
  const id = pixelId()
  if (!id) return false

  if (!window.fbq) {
    // Stub equivalente al loader oficial: encola llamadas en `.queue` hasta
    // que `fbevents.js` carga y las consume. Inyectado por código (no snippet
    // fijo en <head>) para respetar el gate de consentimiento.
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args)
      else fbq.queue.push(args)
    } as FbqFn
    fbq.queue = []
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = SCRIPT_SRC
    document.head.appendChild(script)
  }

  // `autoConfig` (on por defecto en fbevents.js) registra solos los clics en
  // botones (`SubscribedButtonClick`) con su texto y metadatos de la página: en
  // Match&Play ese texto puede llevar nicks o nombres de partida, y no está en la
  // política. Solo queremos nuestros eventos explícitos. Debe ir ANTES del init.
  window.fbq('set', 'autoConfig', false, id)
  window.fbq('init', id)
  window.fbq('track', 'PageView')
  initialized = true
  return true
}

/**
 * El user retiró el consentimiento de publicidad durante la sesión: el
 * script ya cargado deja de enviar eventos. En la próxima carga ni se inyecta.
 */
export function revokeMetaPixel(): void {
  if (!initialized) return
  window.fbq?.('consent', 'revoke')
}

/** Evento estándar de Meta (`track`) o personalizado (`trackCustom`). */
export interface MetaEvent {
  name: string
  custom: boolean
}

/** Envía un evento al píxel. No-op si no está cargado (sin consent / no prod). */
export function trackMetaEvent(event: MetaEvent): void {
  if (!initialized) return
  window.fbq?.(event.custom ? 'trackCustom' : 'track', event.name)
}
